import {
  useState,
  useEffect,
} from 'react';
import { toast } from 'sonner';
import {
  User,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Globe,
  Key,
  Save,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  USER_ROLE_LABELS,
  type UserRole,
} from '@/features/settings';
import { updateUserAttributes } from 'aws-amplify/auth';
import { ChangePasswordModal } from './change-password-modal';

const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
};

function getLanguageLabel(code: string): string {
  return LANGUAGE_LABELS[code] ?? 'English';
}

function getRoleLabel(group: UserRole | undefined): string {
  if (!group) return 'User';
  return USER_ROLE_LABELS[group] ?? group;
}

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  jobTitle: string;
  preferredLanguage: string;
}

export function ProfilePage() {
  const { user } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    jobTitle: '',
    preferredLanguage: 'en',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.givenName ?? '',
        lastName: user.familyName ?? '',
        phoneNumber: user.phoneNumber ?? '',
        jobTitle: user.jobTitle ?? '',
        preferredLanguage: user.preferredLanguage ?? 'en',
      });
    }
  }, [user]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const primaryGroup = user.groups[0] as UserRole | undefined;
  const roleLabel = getRoleLabel(primaryGroup);

  const handleCancel = () => {
    setFormData({
      firstName: user.givenName ?? '',
      lastName: user.familyName ?? '',
      phoneNumber: user.phoneNumber ?? '',
      jobTitle: user.jobTitle ?? '',
      preferredLanguage: user.preferredLanguage ?? 'en',
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateUserAttributes({
        userAttributes: {
          given_name: formData.firstName,
          family_name: formData.lastName,
          phone_number: formData.phoneNumber || undefined,
          'custom:jobTitle': formData.jobTitle || undefined,
          'custom:preferredLanguage': formData.preferredLanguage,
        },
      });
      toast.success('Profile updated successfully. Please refresh to see changes.');
      setIsEditing(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const displayName = formData.firstName || formData.lastName
    ? `${formData.firstName} ${formData.lastName}`.trim()
    : user.email;

  const avatarInitials = `${(formData.firstName[0] ?? user.email[0] ?? '?').toUpperCase()}${(formData.lastName[0] ?? '').toUpperCase()}`;

  const siteAccessLabel = user.siteAccess.length === 0 || user.siteAccess.includes('*')
    ? 'All Sites'
    : `${user.siteAccess.length} site(s)`;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold">My Profile</h1>
        <p className="text-muted-foreground">View and manage your account settings</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Personal Information</CardTitle>
          <ProfileActions
            isEditing={isEditing}
            isSaving={isSaving}
            onEdit={() => setIsEditing(true)}
            onCancel={handleCancel}
            onSave={() => void handleSave()}
          />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary text-2xl font-semibold">
              {avatarInitials}
            </div>
            <div className="flex-1">
              {isEditing ? (
                <NameEditFields
                  firstName={formData.firstName}
                  lastName={formData.lastName}
                  onFirstNameChange={(v) => updateField('firstName', v)}
                  onLastNameChange={(v) => updateField('lastName', v)}
                />
              ) : (
                <>
                  <h2 className="text-xl font-semibold">{displayName}</h2>
                  <p className="text-muted-foreground">{roleLabel}</p>
                </>
              )}
            </div>
          </div>

          <div className="grid gap-4 border-t pt-4">
            <InfoRow icon={Mail} label="Email" value={user.email} />

            <InfoRow
              icon={Phone}
              label="Phone"
              value={formData.phoneNumber || 'Not set'}
              isEditing={isEditing}
              editElement={
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => updateField('phoneNumber', e.target.value)}
                  placeholder="Enter phone number"
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              }
            />

            <InfoRow
              icon={Briefcase}
              label="Job Title"
              value={formData.jobTitle || 'Not set'}
              isEditing={isEditing}
              editElement={
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => updateField('jobTitle', e.target.value)}
                  placeholder="Enter job title"
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              }
            />

            <InfoRow
              icon={Globe}
              label="Preferred Language"
              value={getLanguageLabel(formData.preferredLanguage)}
              isEditing={isEditing}
              editElement={
                <select
                  value={formData.preferredLanguage}
                  onChange={(e) => updateField('preferredLanguage', e.target.value)}
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Access & Permissions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow icon={User} label="Role" value={roleLabel} />
          <InfoRow icon={Building2} label="Organization" value={user.organizationId || 'Not set'} />
          <InfoRow icon={Building2} label="Site Access" value={siteAccessLabel} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Key className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Password</p>
                <p className="text-sm text-muted-foreground">Change your account password</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setShowPasswordModal(true)}>
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
}

interface ProfileActionsProps {
  readonly isEditing: boolean;
  readonly isSaving: boolean;
  readonly onEdit: () => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}

function ProfileActions({
  isEditing,
  isSaving,
  onEdit,
  onCancel,
  onSave,
}: ProfileActionsProps) {
  if (isEditing) {
    return (
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save
        </Button>
      </div>
    );
  }

  return (
    <Button variant="outline" size="sm" onClick={onEdit}>
      Edit Profile
    </Button>
  );
}

interface NameEditFieldsProps {
  readonly firstName: string;
  readonly lastName: string;
  readonly onFirstNameChange: (value: string) => void;
  readonly onLastNameChange: (value: string) => void;
}

function NameEditFields({
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
}: NameEditFieldsProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="text-sm font-medium text-muted-foreground">First Name</label>
        <input
          type="text"
          value={firstName}
          onChange={(e) => onFirstNameChange(e.target.value)}
          className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-muted-foreground">Last Name</label>
        <input
          type="text"
          value={lastName}
          onChange={(e) => onLastNameChange(e.target.value)}
          className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
    </div>
  );
}

interface InfoRowProps {
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly label: string;
  readonly value: string;
  readonly isEditing?: boolean;
  readonly editElement?: React.ReactNode;
}

function InfoRow({
  icon,
  label,
  value,
  isEditing,
  editElement,
}: InfoRowProps) {
  const IconComponent = icon;
  return (
    <div className="flex items-center gap-3">
      <IconComponent className="h-5 w-5 text-muted-foreground" />
      <div className="flex-1">
        <p className="text-sm text-muted-foreground">{label}</p>
        {isEditing && editElement ? editElement : <p className="font-medium">{value}</p>}
      </div>
    </div>
  );
}
